import { ButtonType, Dropdown, EpicIcons } from "@constellation-academy/epic-ui";
import { useFormik } from "formik";
import { useFragment, useMutation } from "react-relay";
import { toast } from "react-toastify";
import { toFormikValidationSchema } from "zod-formik-adapter";
import { DefaultTextFieldComponent } from "@components/DefaultTextInput";
import { useTranslation } from "@i18n/hooks";
import { AccountType } from "@relay/editLeadStrategyForm_AccountFragment.graphql";
import { editLeadStrategyForm_SetAccountTypeMutation } from "@relay/editLeadStrategyForm_SetAccountTypeMutation.graphql";
import { ACCOUNT_FRAGMENT, SET_ACCOUNT_TYPE_MUTATION } from "./edit-lead-strategy-form.graphql";
import { EditLeadStrategyFormSchema } from "./edit-lead-strategy-form.schema";
import {
	EditLeadStrategyFormProps,
	EditLeadStrategyFormState,
} from "./edit-lead-strategy-form.types";
import { Button } from "../../../../components/button";
import { ValidatedField } from "../../../../components/ValidatedField";

export const EditLeadStrategyForm = ({ accountFragmentRef }: EditLeadStrategyFormProps) => {
	const { t } = useTranslation(["common", "errors"]);

	const account = useFragment(ACCOUNT_FRAGMENT, accountFragmentRef);

	const [setAccountType, isSetAccountTypePending] =
		useMutation<editLeadStrategyForm_SetAccountTypeMutation>(SET_ACCOUNT_TYPE_MUTATION);

	const formik = useFormik<EditLeadStrategyFormState>({
		initialValues: {
			leadStrategy: account?.extension?.accountType ?? "ProductLed",
		},
		validationSchema: toFormikValidationSchema(EditLeadStrategyFormSchema),
		onSubmit: (values) => {
			if (!account) return;

			setAccountType({
				variables: {
					input: {
						id: account.id,
						accountType: values.leadStrategy,
						domain: values.domain,
					},
				},
				onCompleted: () => {
					toast.success(t("common:accounts.accountTypeSuccess"));
				},
				onError: () => {
					toast.error(t("errors:accountTypeSaveError"));
				},
			});
		},
	});

	return (
		<form onSubmit={formik.handleSubmit}>
			<ValidatedField<EditLeadStrategyFormState, AccountType>
				name="leadStrategy"
				component={({ fieldValue, updateField, onChange }) => (
					<Dropdown
						options={[
							{ label: t("common:accounts.productLed"), value: "ProductLed" },
							{ label: t("common:accounts.salesLed"), value: "SalesLed" },
						]}
						value={fieldValue}
						onChange={(e) => {
							onChange?.();
							updateField(e as AccountType);
						}}
					/>
				)}
				formikConfig={formik}
			/>
			<ValidatedField<EditLeadStrategyFormState, string>
				name="domain"
				component={DefaultTextFieldComponent}
				required={false}
				formikConfig={formik}
			/>
			<Button
				type={ButtonType.Submit}
				label={t("common:actions.save")}
				icon={EpicIcons.SAVE}
				loading={isSetAccountTypePending}
			/>
		</form>
	);
};
